// ---------------------------------------------------------------
// Case-file manifest + per-variant composition compositor.
// ---------------------------------------------------------------
// Every one of the 10 case-file PNG variants has its own hand-audited
// layer of decorations baked in (paper clips, binder clips, stamps,
// coffee rings, torn corners, filing tabs, handwritten annotations).
// The `obstacles` array records the rectangle each decoration occupies
// as { x, y, w, h } in percent of the container (x/w along the width,
// y/h along the height).
//
// `computeSafeRect(variantKey)` runs a maximum-area empty-rectangle
// search over the complement of the union of those obstacles. The
// result is the actual usable writing surface for that specific
// dossier — narrow for crowded folders, wide for uncluttered ones,
// vertically-oriented when decorations line the top/bottom, etc.
//
// Priority order: readability > natural composition > realism >
// decoration. Bounds are inflated by `margin` (default 2 %) before
// the search so text never kisses the edge of an artifact.
// ---------------------------------------------------------------

const CASE_FILE_MANIFEST = {
  "case-file-01": {
    w: 1460, h: 774, aspect: 1.886,
    obstacles: [
      { name: "clip",    x: 0,  y: 0,  w: 16, h: 25 },
      { name: "stamp",   x: 60, y: 2,  w: 40, h: 25 },
      { name: "coffee",  x: 58, y: 44, w: 42, h: 48 },
      { name: "torn_L",  x: 0,  y: 0,  w: 3,  h: 100 },
    ],
  },
  "case-file-02": {
    w: 1204, h: 774, aspect: 1.556,
    obstacles: [
      { name: "clip",             x: 3,  y: 0,  w: 16, h: 22 },
      { name: "handwrite_decl77", x: 1,  y: 22, w: 32, h: 12 },
      { name: "stamp_ts",         x: 42, y: 2,  w: 28, h: 24 },
      { name: "coffee",           x: 38, y: 60, w: 32, h: 30 },
      // Phantom obstacle — the source PNG for this one variant
      // has ~30 % transparent bleed on the right side (alpha
      // bbox stops at ~70 % of element width). Placing text
      // beyond that lands on masked-away pixels. Treating the
      // dead zone as an unfillable obstacle keeps the algorithm
      // inside the visible paper.
      { name: "dead_right_margin",  x: 70, y: 0,  w: 30, h: 100 },
      { name: "dead_bottom_margin", x: 0,  y: 89, w: 100, h: 11 },
    ],
  },
  "case-file-03": {
    w: 973, h: 753, aspect: 1.292,
    obstacles: [
      { name: "clip",       x: 10, y: 0,  w: 15, h: 26 },
      { name: "stamp_decl", x: 58, y: 2,  w: 42, h: 24 },
      { name: "tag_number", x: 2,  y: 78, w: 28, h: 16 },
      { name: "burn_R",     x: 92, y: 40, w: 8,  h: 18 },
    ],
  },
  "case-file-04": {
    w: 1515, h: 789, aspect: 1.920,
    obstacles: [
      { name: "clip_tape", x: 6,  y: 0,  w: 28, h: 22 },
      { name: "tab_ar",    x: 62, y: 0,  w: 38, h: 14 },
      { name: "pencil_L",  x: 0,  y: 22, w: 22, h: 42 },
      { name: "torn_bot",  x: 0,  y: 94, w: 100, h: 6 },
    ],
  },
  "case-file-05": {
    w: 1095, h: 762, aspect: 1.437,
    obstacles: [
      { name: "clip",       x: 4,  y: 0,  w: 15, h: 22 },
      { name: "stamp_conf", x: 60, y: 3,  w: 40, h: 26 },
      { name: "coffee_BR",  x: 52, y: 58, w: 42, h: 32 },
      { name: "torn_bot",   x: 0,  y: 94, w: 100, h: 6 },
    ],
  },
  "case-file-06": {
    w: 1530, h: 801, aspect: 1.910,
    obstacles: [
      { name: "clip",         x: 12, y: 2,  w: 14, h: 30 },
      { name: "ink_blots_TR", x: 80, y: 0,  w: 20, h: 30 },
      { name: "tape_BL",      x: 0,  y: 58, w: 30, h: 34 },
      { name: "fold_BR",      x: 80, y: 62, w: 20, h: 32 },
    ],
  },
  "case-file-07": {
    w: 1501, h: 738, aspect: 2.034,
    obstacles: [
      { name: "handwrite_top", x: 2,  y: 0,  w: 34, h: 14 },
      { name: "binder_TR",     x: 82, y: 0,  w: 18, h: 32 },
      { name: "stamp_filed_R", x: 84, y: 32, w: 16, h: 22 },
      { name: "stamp_conf_BL", x: 2,  y: 56, w: 40, h: 26 },
    ],
  },
  "case-file-08": {
    w: 1035, h: 827, aspect: 1.252,
    obstacles: [
      { name: "torn_TL",       x: 0,  y: 0,  w: 15, h: 20 },
      { name: "handwrite_L",   x: 0,  y: 22, w: 8,  h: 50 },
      { name: "stamp_decl_TR", x: 58, y: 2,  w: 42, h: 24 },
      { name: "burn_BR",       x: 52, y: 66, w: 26, h: 22 },
    ],
  },
  "case-file-09": {
    w: 1187, h: 784, aspect: 1.514,
    obstacles: [
      { name: "clip",             x: 8,  y: 2,  w: 14, h: 30 },
      { name: "stamps_ts_TR",     x: 56, y: 2,  w: 42, h: 28 },
      { name: "stamp_ref_R",      x: 82, y: 36, w: 18, h: 24 },
      { name: "handwrite_BL",     x: 2,  y: 66, w: 46, h: 18 },
    ],
  },
  "case-file-10": {
    w: 1486, h: 749, aspect: 1.984,
    obstacles: [
      { name: "stamp_decl_TL", x: 14, y: 2,  w: 32, h: 22 },
      { name: "binder_L",      x: 0,  y: 38, w: 22, h: 26 },
      { name: "handwrite_R",   x: 86, y: 18, w: 12, h: 54 },
      { name: "folded_TR",     x: 86, y: 0,  w: 14, h: 18 },
      { name: "stamp_rec_BR",  x: 56, y: 66, w: 22, h: 22 },
    ],
  },
};

// ---------------------------------------------------------------
// Maximal empty axis-aligned rectangle — returns the largest
// obstacle-free rectangle inside [0..100, 0..100] as
// { x, y, w, h }. Prefers landscape-ish shapes so headline text
// wraps naturally rather than into a tall thin sliver.
//
// The score is `area * aspectFit` where aspectFit rewards
// wide-to-square rectangles (headline copy reads best in a
// landscape band) and heavily penalises tall slivers. Small
// slivers below MIN_W / MIN_H are rejected outright.
//
// Memoised per (variantKey + margin) — the manifest is static.
// ---------------------------------------------------------------
const _rectCache = new Map();

const MIN_W = 28;   // % of container width  — narrower reads as a column
const MIN_H = 22;   // % of container height — shorter can't fit title + tagline
// Visible-paper inset. The case-file PNGs are exported with a small
// transparent bleed on every side (the Pillow bbox-crop pass leaves
// ~2.5 % breathing room so the alpha drop-shadow filter has room to
// bleed cleanly). Text placed inside that transparent margin lands
// OUTSIDE the visible paper. Every safe rectangle we compute is
// therefore constrained to `[EDGE_INSET .. 100 - EDGE_INSET]` in both
// axes, and the top/left edges get an extra `EDGE_MARGIN` for
// typographic breathing room off the paper edge itself.
const EDGE_INSET  = 6.0;   // hard boundary — paper alpha ends around here
const EDGE_MARGIN = 3.0;   // additional breathing room from the paper edge

export function computeSafeRect(variantKey, opts = {}) {
  const margin = opts.margin ?? 2.0;
  const preferRatio = opts.preferRatio ?? 1.6; // ideal w/h for headline copy
  const ck = `${variantKey}::${margin}::${preferRatio}`;
  if (_rectCache.has(ck)) return _rectCache.get(ck);

  const xMin = EDGE_INSET + EDGE_MARGIN;   // ~5
  const xMax = 100 - EDGE_INSET - EDGE_MARGIN;
  const yMin = EDGE_INSET + EDGE_MARGIN;
  const yMax = 100 - EDGE_INSET - EDGE_MARGIN;

  const meta = CASE_FILE_MANIFEST[variantKey];
  if (!meta) {
    const fb = { x: xMin, y: yMin, w: xMax - xMin, h: yMax - yMin, aspect: (xMax - xMin) / (yMax - yMin) };
    _rectCache.set(ck, fb);
    return fb;
  }

  const inflated = meta.obstacles.map((o) => {
    const x1 = Math.max(xMin, o.x - margin);
    const y1 = Math.max(yMin, o.y - margin);
    const x2 = Math.min(xMax, o.x + o.w + margin);
    const y2 = Math.min(yMax, o.y + o.h + margin);
    return { x: x1, y: y1, w: Math.max(0, x2 - x1), h: Math.max(0, y2 - y1) };
  }).filter((o) => o.w > 0 && o.h > 0);

  const xs = new Set([xMin, xMax]);
  const ys = new Set([yMin, yMax]);
  inflated.forEach((o) => {
    xs.add(o.x); xs.add(o.x + o.w);
    ys.add(o.y); ys.add(o.y + o.h);
  });
  const xa = [...xs].filter((v) => v >= xMin && v <= xMax).sort((a, b) => a - b);
  const ya = [...ys].filter((v) => v >= yMin && v <= yMax).sort((a, b) => a - b);

  const _overlaps = (x1, y1, x2, y2) => {
    for (let m = 0; m < inflated.length; m++) {
      const o = inflated[m];
      if (o.x < x2 && o.x + o.w > x1 && o.y < y2 && o.y + o.h > y1) return true;
    }
    return false;
  };

  let best = { area: 0, score: 0, x: xMin, y: yMin, w: xMax - xMin, h: yMax - yMin };
  for (let i = 0; i < xa.length; i++) {
    for (let j = i + 1; j < xa.length; j++) {
      const x1 = xa[i], x2 = xa[j];
      const w = x2 - x1;
      if (w < MIN_W) continue;
      for (let k = 0; k < ya.length; k++) {
        for (let l = k + 1; l < ya.length; l++) {
          const y1 = ya[k], y2 = ya[l];
          const h = y2 - y1;
          if (h < MIN_H) continue;
          if (_overlaps(x1, y1, x2, y2)) continue;
          const area = w * h;
          const ratio = w / h;
          // aspectFit — peaks at preferRatio, decays smoothly either side.
          // Portrait rects (ratio < 1) get pushed down hard; wide bands
          // (ratio around 1.5–2.5) get near-full credit.
          const r = ratio / preferRatio;
          const aspectFit = r <= 1 ? Math.pow(r, 1.15) : Math.pow(1 / r, 0.35);
          const score = area * aspectFit;
          if (score > best.score) best = { area, score, x: x1, y: y1, w, h };
        }
      }
    }
  }

  best.aspect = best.w / best.h;
  _rectCache.set(ck, best);
  return best;
}

/**
 * Convert a safe rectangle (% of container width/height) to CSS
 * padding values scaled so that vertical percentages are
 * *aspect-corrected*: CSS % padding always resolves against the
 * container WIDTH, so we divide the vertical %s by the container's
 * paper aspect ratio to keep the rectangle geometrically correct.
 *
 * Returns { paddingTop, paddingRight, paddingBottom, paddingLeft }
 * as CSS strings ready to spread into an inline style.
 */
export function safeRectAsPadding(variantKey, opts = {}) {
  const meta = CASE_FILE_MANIFEST[variantKey];
  const aspect = meta?.aspect ?? 1.78;
  const rect = computeSafeRect(variantKey, opts);
  const padTopPct    = rect.y / aspect;
  const padBotPct    = (100 - rect.y - rect.h) / aspect;
  const padLeftPct   = rect.x;
  const padRightPct  = 100 - rect.x - rect.w;
  return {
    paddingTop:    `${padTopPct.toFixed(2)}%`,
    paddingRight:  `${padRightPct.toFixed(2)}%`,
    paddingBottom: `${padBotPct.toFixed(2)}%`,
    paddingLeft:   `${padLeftPct.toFixed(2)}%`,
    // Also expose rect meta for downstream typography scaling.
    _rect: rect,
    _aspect: aspect,
  };
}

export default CASE_FILE_MANIFEST;
